/**
 * Google Analytics 4 (GA4) - Integración con gtag.js
 * Tracking completo: vistas de página, navegación de guías, editor, pasaporte,
 * tarjeta viajera, compras, y todas las interacciones del usuario.
 *
 * Configuración: REACT_APP_GA_MEASUREMENT_ID en .env (ej: G-XXXXXXXXXX)
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const isEnabled = () =>
	typeof window !== "undefined" &&
	GA_MEASUREMENT_ID &&
	GA_MEASUREMENT_ID.startsWith("G-");

export function init() {
	if (!isEnabled()) return;
	if (window.gtag) {
		window.gtag("config", GA_MEASUREMENT_ID, {
			send_page_view: false,
		});
		return;
	}
	const script = document.createElement("script");
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
	script.async = true;
	document.head.appendChild(script);

	window.dataLayer = window.dataLayer || [];
	window.gtag = function () {
		window.dataLayer.push(arguments);
	};
	window.gtag("js", new Date());
	window.gtag("config", GA_MEASUREMENT_ID, {
		send_page_view: false,
	});
}

export function trackPageView(path, title) {
	if (!isEnabled() || !window.gtag) return;
	window.gtag("event", "page_view", {
		page_path: path || window.location.pathname,
		page_title: title || document.title,
	});
}

export function trackEvent(eventName, params = {}) {
	if (!isEnabled() || !window.gtag) return;
	window.gtag("event", eventName, params);
}

export function setUserId(userId) {
	if (!isEnabled() || !window.gtag) return;
	window.gtag("config", GA_MEASUREMENT_ID, {
		user_id: String(userId),
	});
}

export function clearUserId() {
	if (!isEnabled() || !window.gtag) return;
	window.gtag("config", GA_MEASUREMENT_ID, {
		user_id: null,
	});
}

// ── Mapas de rutas a títulos legibles para page_view ──

const ROUTE_TITLES = {
	"/": "Inicio",
	"/explorar": "Guías",
	"/destinos": "Destinos",
	"/mis-guias": "Mis guías",
	"/favoritos": "Favoritos",
	"/passport": "Mi Pasaporte",
	"/tarjeta-viajera": "Tarjeta viajera",
	"/modulos": "Módulos",
	"/descuentos": "Descuentos",
	"/profile": "Perfil",
	"/settings": "Configuración",
	"/payment/success": "Pago exitoso",
	"/payment/cancel": "Pago cancelado",
	"/verify-email": "Verificar email",
	"/forgot-password": "Olvidé contraseña",
	"/reset-password": "Restablecer contraseña",
	"/legal/aviso-legal": "Aviso legal",
	"/legal/privacidad": "Privacidad",
	"/legal/terminos": "Términos",
	"/legal/cookies": "Cookies",
};

/**
 * Genera un título legible a partir de la ruta.
 * Reconoce rutas dinámicas como /preview/prebuilt/:guideId y /view/:token.
 */
export function getTitleForPath(path) {
	if (ROUTE_TITLES[path]) return ROUTE_TITLES[path];
	if (path.startsWith("/guia/")) {
		const slug = path.split("/")[2] || "";
		return `Ficha guía: ${slug}`;
	}
	const pathSlug = path.replace(/^\//, "").split("/")[0];
	if (
		pathSlug &&
		path.split("/").filter(Boolean).length === 1 &&
		[
			"kioto",
			"tokio",
			"osaka",
			"nueva-york",
			"san-francisco",
			"londres",
			"paris",
			"seul",
			"bangkok",
			"kuala-lumpur",
			"belgica",
			"pekin",
			"bali",
		].includes(pathSlug)
	) {
		return `Ficha guía: ${pathSlug}`;
	}
	if (path.startsWith("/preview/prebuilt/")) {
		const guideId = path.split("/")[3] || "";
		return `Vista previa: ${guideId}`;
	}
	if (path.startsWith("/view/")) return "Viewer guía compartida";
	if (path.startsWith("/passport/view/")) return "Viewer pasaporte compartido";
	if (path.startsWith("/usuarios/") && path.includes("/guias/"))
		return "Editor de guía (StickerApp)";
	return path;
}

/**
 * Envía page_view con título legible automático.
 * Llamar desde RouteTracker en cada cambio de ruta.
 */
export function trackSmartPageView(path) {
	if (!isEnabled() || !window.gtag) return;
	const title = getTitleForPath(path);
	window.gtag("event", "page_view", {
		page_path: path,
		page_title: title,
	});
}

// ── Helpers para eventos frecuentes ──

export function trackGuideInteraction(action, guideId, extra = {}) {
	trackEvent(`guide_${action}`, {
		guide_id: guideId ? String(guideId) : undefined,
		...extra,
	});
}

export function trackEditorAction(action, guideId, extra = {}) {
	trackEvent(`editor_${action}`, {
		guide_id: guideId ? String(guideId) : undefined,
		...extra,
	});
}

export function trackPassportAction(action, extra = {}) {
	trackEvent(`passport_${action}`, extra);
}

export function trackTravelCardAction(action, extra = {}) {
	trackEvent(`travel_card_${action}`, extra);
}

export function trackPurchaseAction(action, extra = {}) {
	trackEvent(`purchase_${action}`, extra);
}

export default {
	init,
	trackPageView,
	trackSmartPageView,
	trackEvent,
	setUserId,
	clearUserId,
	getTitleForPath,
	trackGuideInteraction,
	trackEditorAction,
	trackPassportAction,
	trackTravelCardAction,
	trackPurchaseAction,
};
